"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui-lab/button"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui-lab/sheet"
import { HiMenu } from "react-icons/hi"
import Link from "next/link"

interface NavItem {
  id: string
  label: string
  href?: string
}

const navItems: NavItem[] = [
  { id: "about", label: "About" },
  { id: "skills", label: "Skills" },
  { id: "experience", label: "Experience" },
  { id: "education", label: "Education" },
  { id: "projects", label: "Projects" },
  { id: "contact", label: "Contact" },
  { id: "location", label: "Location" },
]

export const NavigationBar = () => {
  const [activeSection, setActiveSection] = useState("about")
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Among all currently intersecting sections, pick the one highest on the page
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id)
        }
      },
      {
        // Active zone: a horizontal band from 10% to 40% down the viewport
        rootMargin: "-10% 0px -60% 0px",
        threshold: 0,
      }
    )

    navItems.forEach((item) => {
      const el = document.getElementById(item.id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 80
      const elementPosition = element.offsetTop - offset
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      })
    }
    setMobileMenuOpen(false)
  }

  return (
    <>
      {/* Desktop Navigation */}
      <nav aria-label="Main navigation" className="hidden md:flex items-center space-x-1 gap-1">
        {navItems.map((item) =>
          item.href ? (
            <Link
              key={item.id}
              href={item.href}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-105 hover:shadow-md"
            >
              {item.label}
            </Link>
          ) : (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => {
                e.preventDefault()
                scrollToSection(item.id)
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                activeSection === item.id
                  ? "bg-teal-700 text-white shadow-lg scale-105"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-105 hover:shadow-md"
              }`}
            >
              {item.label}
            </a>
          )
        )}
      </nav>

      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden transition-all duration-300 hover:scale-110 hover:bg-gray-100 dark:hover:bg-gray-800 mt-1"
        onClick={() => setMobileMenuOpen(true)}
        aria-label="Open navigation menu"
      >
        <HiMenu className="text-xl" />
      </Button>

      {/* Mobile Drawer */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-[280px] sm:max-w-[280px]">
          <SheetHeader className="mb-4">
            <SheetTitle>Navigation</SheetTitle>
            <SheetDescription className="sr-only">Jump to a section of the page</SheetDescription>
          </SheetHeader>
          <div className="flex flex-col space-y-2">
            {navItems.map((item) =>
              item.href ? (
                <Link
                  key={item.id}
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg text-left font-medium transition-all duration-300 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-105 hover:translate-x-2"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => {
                    e.preventDefault()
                    scrollToSection(item.id)
                  }}
                  className={`px-4 py-3 rounded-lg text-left font-medium transition-all duration-300 ${
                    activeSection === item.id
                      ? "bg-teal-700 text-white scale-105"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-105 hover:translate-x-2"
                  }`}
                >
                  {item.label}
                </a>
              )
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
